/**
 * Runtime theme engine (Phase 1).
 *
 * The app's colour tokens are Tailwind v4 `@theme` variables (see
 * `styles/globals.css`); every utility (`bg-bg`, `text-brand`, `border-border`,
 * …) resolves `var(--color-*)`. Setting those vars inline on `:root` overrides
 * the build-time values, so a theme change recolours the whole app instantly
 * with no rebuild.
 *
 * Phase 1 ships named presets plus a `custom` palette derived from a
 * user-picked background + accent. Prefs are persisted locally (localStorage),
 * mirroring the `useGameSelection` + `storage.ts` pattern. Phase 3 will sync to
 * the customization-backend and unlock themes via gamification / AI.
 */

import { mix, relativeLuminance } from './color';
import { storage, StorageKey } from './storage';
import { setTelegramChrome } from './telegram';

/** The CSS variables the engine drives. */
export type TokenName =
  | '--color-bg'
  | '--color-section'
  | '--color-bg-secondary'
  | '--color-bg-tertiary'
  | '--color-fg'
  | '--color-fg-muted'
  | '--color-border'
  | '--color-brand'
  | '--color-brand-fg';

export type Palette = Record<TokenName, string>;

export interface AppearancePrefs {
  /** Preset id, or `'custom'` to use customBg/customAccent. */
  presetId: string;
  customBg?: string;
  customAccent?: string;
}

export interface ThemePreset {
  id: string;
  /** Display name — kept brand-literal (not translated). */
  name: string;
  palette: Palette;
}

const CYAN = '#00e7df';
const LIGHT_FG = '#e0e3e2';
const DARK_FG = '#0b0f0f';

/**
 * Build a coherent dark palette from a base background + accent colour.
 * Surfaces/border are lifted toward the foreground; foreground and
 * text-on-accent flip by luminance so any user-picked colour stays legible.
 */
export function derivePalette(bg: string, accent: string): Palette {
  const fg = relativeLuminance(bg) > 0.55 ? DARK_FG : LIGHT_FG;
  const accentFg = relativeLuminance(accent) > 0.6 ? '#06201f' : '#ffffff';
  return {
    '--color-bg': bg,
    '--color-section': bg,
    '--color-bg-secondary': mix(bg, fg, 0.08),
    '--color-bg-tertiary': mix(bg, fg, 0.16),
    '--color-fg': fg,
    '--color-fg-muted': mix(fg, bg, 0.4),
    '--color-border': mix(bg, fg, 0.22),
    '--color-brand': accent,
    '--color-brand-fg': accentFg,
  };
}

/** Le superfici verde acqua, condivise da tutti i temi. */
const TEAL_SURFACES = {
  '--color-bg': '#04302d',
  '--color-section': '#04302d',
  '--color-bg-secondary': '#0b4340',
  '--color-bg-tertiary': '#125651',
  '--color-fg': '#e6f4f2',
  '--color-fg-muted': '#7fbdb8',
  '--color-border': '#1b5f59',
} as const;

/**
 * Un tema = le superfici teal + un accento.
 *
 * `--color-brand-fg` (il testo SOPRA l'accento) si sceglie per luminanza, non
 * a mano: un accento chiaro come il giallo vuole testo scuro, uno saturo come
 * il rosso lo vuole bianco. Fissarlo produrrebbe un tema illeggibile ogni
 * volta che si aggiunge un colore dall'altro capo della scala.
 */
function tealSurfaces(id: string, name: string, accent: string): ThemePreset {
  return {
    id,
    name,
    palette: {
      ...TEAL_SURFACES,
      '--color-brand': accent,
      '--color-brand-fg': relativeLuminance(accent) > 0.5 ? '#04302d' : '#ffffff',
    },
  };
}

export const PRESETS: ThemePreset[] = [
  {
    // IL PREDEFINITO. Base verde acqua profondo, in accordo con la texture a
    // scaglie dello sfondo (public/bg-scales.jpg).
    //
    // Perché non i grigi di prima: le superfici erano neutre (#191c1c, #282828)
    // e i testi spenti grigi (#9a9a9a). Su fondo nero funzionavano; sopra la
    // texture verde acqua stonano — un grigio neutro accanto a un teal saturo
    // si legge come sporco, non come neutro. Qui ogni superficie porta la
    // stessa dominante del fondo, così le schede sembrano ritagliate nello
    // sfondo invece che appoggiate sopra.
    id: 'qlash-teal',
    name: 'QLASH Teal',
    palette: {
      '--color-bg': '#04302d',
      '--color-section': '#04302d',
      '--color-bg-secondary': '#0b4340',
      '--color-bg-tertiary': '#125651',
      '--color-fg': '#e6f4f2',
      // Teal desaturato, NON grigio: è il colore delle icone e delle etichette
      // inattive nella navigazione, e in grigio sembravano disabilitate.
      '--color-fg-muted': '#7fbdb8',
      '--color-border': '#1b5f59',
      '--color-brand': CYAN,
      '--color-brand-fg': '#003737',
    },
  },
  // Le altre varianti cambiano SOLO l'accento. Le superfici restano le stesse
  // del predefinito perché lo SFONDO dell'app è un'immagine verde acqua fissa
  // (public/bg-scales.jpg): una palette con base nera o viola metterebbe
  // superfici fuori tinta sopra una texture che resta comunque teal, e il
  // risultato sarebbe due temi sovrapposti invece di uno.
  //
  // Cambiare l'accento è sufficiente perché l'accento è ciò che l'utente
  // riconosce come "il suo colore": compare su selezione, azioni e stella.
  tealSurfaces('qlash-red', 'QLASH Red', '#ff4d5a'),
  tealSurfaces('qlash-violet', 'QLASH Violet', '#b06cff'),
  tealSurfaces('qlash-lime', 'QLASH Lime', '#9ee94a'),
  tealSurfaces('qlash-gold', 'QLASH Gold', '#ffc75a'),
];

export const DEFAULT_PREFS: AppearancePrefs = { presetId: 'qlash-teal' };
const DEFAULT_PRESET = PRESETS[0];
const PRESET_BY_ID = new Map(PRESETS.map((p) => [p.id, p]));

/** Resolve prefs → a concrete palette. */
export function resolvePalette(prefs: AppearancePrefs): Palette {
  if (prefs.presetId === 'custom') {
    // Anche il tema personalizzato tiene le superfici verde acqua: sceglie solo
    // l'accento. `customBg` resta nel tipo per non invalidare le preferenze già
    // salvate, ma non viene più letto — un fondo libero metterebbe superfici
    // fuori tinta sopra una texture di sfondo che resta comunque teal.
    return tealSurfaces('custom', 'Custom', prefs.customAccent ?? CYAN).palette;
  }
  return (PRESET_BY_ID.get(prefs.presetId) ?? DEFAULT_PRESET).palette;
}

/** Apply a palette to the document + sync the native Telegram chrome. */
export function applyPalette(palette: Palette): void {
  const root = document.documentElement;
  (Object.keys(palette) as TokenName[]).forEach((name) => {
    root.style.setProperty(name, palette[name]);
  });
  setTelegramChrome(palette['--color-bg']);
}

/**
 * Il predefinito PRECEDENTE. Va trattato come "mai scelto", non come una
 * preferenza: `useAppearance` chiama `saveAppearance` a ogni montaggio, quindi
 * ogni utente che ha aperto l'app anche una sola volta se l'è ritrovato scritto
 * in localStorage senza aver toccato nulla. Senza questa migrazione il nuovo
 * tema non arriverebbe a NESSUNO degli utenti esistenti.
 *
 * Costo accettato: chi aveva scelto il nero deliberatamente viene spostato una
 * volta sola. Il valore salvato è indistinguibile fra scelta e automatismo, e
 * ripristinarlo è un tocco in Impostazioni.
 */
const LEGACY_DEFAULT_PRESET_ID = 'qlash-black';

/** Read persisted prefs (localStorage). Falls back to the default. */
export function loadStoredAppearance(): AppearancePrefs {
  const raw = storage.get(StorageKey.Appearance);
  if (!raw) return DEFAULT_PREFS;
  try {
    const parsed = JSON.parse(raw) as Partial<AppearancePrefs>;
    if (parsed?.presetId === LEGACY_DEFAULT_PRESET_ID) return DEFAULT_PREFS;
    if (parsed && typeof parsed.presetId === 'string') {
      return {
        presetId: parsed.presetId,
        customBg: typeof parsed.customBg === 'string' ? parsed.customBg : undefined,
        customAccent:
          typeof parsed.customAccent === 'string' ? parsed.customAccent : undefined,
      };
    }
  } catch {
    /* corrupt value — fall through to default */
  }
  return DEFAULT_PREFS;
}

/** Persist prefs (localStorage). */
export function saveAppearance(prefs: AppearancePrefs): void {
  storage.set(StorageKey.Appearance, JSON.stringify(prefs));
}
